import { useTranslation, TFunction, useBi } from '@wix/yoshi-flow-editor';
import { bookingsBoWaiverAppSrc16Evid66 } from '@wix/bi-logger-bookings-data/v2';
import {
  intakeFormsErrorDefaults,
  intakeFormsErrorSuffixes,
} from '../../../utils/bi/consts';
import { FormViewerHandle } from '@wix/form-viewer/widget';
import { getServiceSlotIdentifier, mapToArray } from '../../../utils';
import {
  ServiceData,
  SlotService,
  IntakeForms,
  IntakeForm,
  IntakeFormStatus,
} from '../../../utils/state/types';
import { PaymentOption } from '../../../types/types';
import { getMaxNumberOfParticipants } from '../../../utils/numberOfParticipant';
import { DynamicPriceInfo } from '../../../types/dynamicPrice';
import {
  DynamicPriceErrorType,
  GenericErrorType,
  IntakeFormErrorType,
} from '../../../types/errors';
import { AddError } from '../Actions/errorHandlers/addError/addError';
import { FormControllerActions } from '../Actions/actions';
import { hasEmailMismatchWithIntakeForms } from '../../../utils/intakeFormUtils';
import { WixFormFields } from '../../../types/wix-forms';
import { FormValues } from '@wix/form-viewer';

type ValidateWixForm = {
  actions: FormControllerActions;
  serviceData: ServiceData;
  wixFormRef: React.RefObject<FormViewerHandle | undefined>;
  intakeForms?: IntakeForms;
  wixFormValues?: FormValues;
};

export default function useValidateWixForm() {
  const { t } = useTranslation();
  const bi = useBi();

  const validateWixForm = async ({
    actions,
    serviceData,
    wixFormRef,
    intakeForms,
    wixFormValues,
  }: ValidateWixForm): Promise<boolean> => {
    const { addError } = actions;
    const isValidWixForm = (await wixFormRef?.current?.validate?.()) || false;

    if (isValidWixForm) {
      const isValidBookingsForm = ValidateBookingsForm({
        serviceData,
        addError,
        t,
      });

      const isValidIntakeForms = ValidateIntakeForms({
        intakeForms,
        addError,
        t,
        email: wixFormValues?.[WixFormFields.EMAIL] as string | undefined,
        bi,
      });

      return isValidBookingsForm && isValidIntakeForms;
    }

    return isValidWixForm;
  };

  return {
    validateWixForm,
  };
}

const ValidateBookingsForm = ({
  serviceData,
  addError,
  t,
}: {
  serviceData: ServiceData;
  addError: AddError;
  t: TFunction;
}) => {
  if (!serviceData.isSingleService) {
    return true;
  }

  const singleSlotService = mapToArray<SlotService>(
    serviceData.slotServices,
  )[0];

  const { selectedPaymentOption } = singleSlotService;
  const isDynamicPricingCustom =
    !!singleSlotService.dynamicPriceInfo?.customOptions?.length;
  if (singleSlotService.dynamicPriceInfo && isDynamicPricingCustom) {
    return isDynamicPriceDropdownValid({
      dynamicPriceInfo: singleSlotService.dynamicPriceInfo,
      selectedPaymentOption,
      currentMaxNumberOfParticipants: getMaxNumberOfParticipants(
        serviceData,
        getServiceSlotIdentifier(singleSlotService.nestedSlot),
      ),
      t,
      addError,
    });
  }

  return true;
};

const isDynamicPriceDropdownValid = ({
  dynamicPriceInfo,
  selectedPaymentOption,
  currentMaxNumberOfParticipants,
  addError,
  t,
}: {
  dynamicPriceInfo: DynamicPriceInfo;
  selectedPaymentOption: PaymentOption;
  currentMaxNumberOfParticipants: number;
  addError: AddError;
  t: TFunction;
}) => {
  const selectedNumberOfParticipants =
    dynamicPriceInfo?.selectedVariants?.reduce(
      (acc, nextVariant) => acc + (nextVariant.numberOfParticipants || 0),
      0,
    ) || 0;

  const isAtLeastOneParticipantSelected = selectedNumberOfParticipants > 0;
  const isEqualToOrLessThanMaxParticipantsAllowed =
    selectedNumberOfParticipants <= currentMaxNumberOfParticipants;

  const isDropdownValid =
    isAtLeastOneParticipantSelected &&
    isEqualToOrLessThanMaxParticipantsAllowed;

  if (!isDropdownValid) {
    if (!isAtLeastOneParticipantSelected) {
      addError(DynamicPriceErrorType.MISSING_REQUIRED_FIELD_DATA, {
        message: t('app.form-inputs.validation-errors.required-field'),
      });
    } else {
      addError(DynamicPriceErrorType.NOT_ENOUGH_SESSIONS_FOR_DYNAMIC_PRICE, {
        message: t(
          'app.booking-details.price-not-enough-sessions-error-message',
          {
            planName: selectedPaymentOption.label,
          },
        ),
      });
    }
  }
  return isAtLeastOneParticipantSelected;
};

const ValidateIntakeForms = ({
  intakeForms,
  addError,
  t,
  email,
  bi,
}: {
  intakeForms?: IntakeForms;
  addError: AddError;
  t: TFunction;
  email?: string;
  bi: ReturnType<typeof useBi>;
}) => {
  if (!intakeForms) {
    return true;
  }

  const intakeFormsArray = mapToArray<IntakeForm>(intakeForms);

  // A submission awaiting approval blocks BOTH "Book" and "Add to Cart" (same gate) so the pending
  // flow can't be bypassed via cart — surfaces the pending dialog (NO_VALID_INTAKE_FORM_SUBMISSION).
  const hasPendingSubmission = intakeFormsArray.some(
    (form) => form.status === IntakeFormStatus.PENDING_APPROVAL,
  );

  if (hasPendingSubmission) {
    addError(GenericErrorType.NO_VALID_INTAKE_FORM_SUBMISSION);
    return false;
  }

  const hasIncompleteRequiredForms = intakeFormsArray.some(
    (form) => form.status === IntakeFormStatus.REQUIRED,
  );

  if (hasIncompleteRequiredForms) {
    addError(IntakeFormErrorType.REQUIRED_INTAKE_FORM_NOT_COMPLETED, {
      message: t(
        'app.intake-form-section.error.required_intake_form_not_completed',
      ),
    });
    bi.report(
      bookingsBoWaiverAppSrc16Evid66({
        ...intakeFormsErrorDefaults,
        value: IntakeFormErrorType.REQUIRED_INTAKE_FORM_NOT_COMPLETED,
      }),
    );
    return false;
  }

  const hasEmailMismatch = hasEmailMismatchWithIntakeForms(email, intakeForms);

  if (hasEmailMismatch) {
    addError(IntakeFormErrorType.EMAIL_MISMATCH_WITH_INTAKE_FORM, {
      message: t(
        'app.intake-form-section.error.email_mismatch_with_intake_form',
      ),
    });
    bi.report(
      bookingsBoWaiverAppSrc16Evid66({
        ...intakeFormsErrorDefaults,
        value: `${IntakeFormErrorType.EMAIL_MISMATCH_WITH_INTAKE_FORM}${intakeFormsErrorSuffixes.BOOK_NOW}`,
      }),
    );
    return false;
  }

  return true;
};
